import axios, { microServices } from './axios';
import { loaders } from './../constants';

const PLATFORM_CONNECTIONS = '/platform-connections';

/**
 * Fetch platform connections for the active authenticated organization account.
 * Routes to de-account (DE) as connections are managed there.
 * @param {boolean} [useOverlay=true] - Whether to display full overlay loader
 * @returns {Promise<Array>} Platform connections list
 */
export const getPlatformConnections = async (useOverlay = true) => {
  return await axios('get', `${PLATFORM_CONNECTIONS}`, {}, {
    loader: useOverlay ? loaders.OVERLAY : loaders.NONE,
    server: microServices.DE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Update platform connection settings.
 * @param {string} id - Connection UUID
 * @param {Object} settings - Settings payload
 * @returns {Promise<Object>} Updated connection response
 */
export const updateConnectionSettings = async (id, settings) => {
  return await axios('put', `${PLATFORM_CONNECTIONS}/${id}/settings`, { settings }, {
    loader: loaders.OVERLAY,
    successMessage: 'Accounting Settings Updated Successfully',
    server: microServices.DE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Toggle platform connection active state.
 * @param {string} id - Connection UUID
 * @param {boolean} isActive - New active status
 * @returns {Promise<Object>} Toggle response
 */
export const toggleConnectionStatus = async (id, isActive) => {
  return await axios('post', `${PLATFORM_CONNECTIONS}/${id}/toggle`, { is_active: isActive }, {
    loader: loaders.OVERLAY,
    successMessage: isActive ? 'Integration Enabled' : 'Integration Disabled',
    server: microServices.DE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Trigger manual Contact syncing (Customers/Suppliers/Contractors).
 * Routes to de-core (DE_CORE) as contact data and sync logic lives there.
 * @param {string} id - Connection UUID
 * @param {string} entityType - Entity type ('customer' | 'supplier' | 'contractor')
 * @param {Array<string>} entityIds - Contact internal IDs
 * @param {string} [direction='push'] - Sync direction ('push' | 'pull')
 * @param {Array} [resolutions] - Conflict resolution payload for pull
 * @param {Object} [options={}] - Request options
 * @returns {Promise<Object>} Sync response
 */
export const syncPlatformContacts = async (id, entityType, entityIds, direction = 'push', resolutions = undefined, options = {}) => {
  return await axios('post', `${PLATFORM_CONNECTIONS}/${id}/sync-contacts`, {
    entity_type: entityType,
    entity_ids: entityIds,
    direction,
    resolutions
  }, {
    loader: options?.loader !== undefined ? options.loader : loaders.NONE,
    successMessage: options?.successMessage !== undefined ? options.successMessage : null,
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Fetch conflict preview for pulling external contacts into Contractable.
 * @param {string} id - Connection UUID
 * @param {string} entityType - Entity type ('customer' | 'supplier' | 'contractor')
 * @param {Array<string>} [entityIds] - Optional contact IDs filter
 * @returns {Promise<Array>} Conflict preview list
 */
export const previewPullContacts = async (id, entityType, entityIds) => {
  return await axios('post', `${PLATFORM_CONNECTIONS}/${id}/sync-contacts/preview-pull`, {
    entity_type: entityType,
    entity_ids: entityIds
  }, {
    loader: loaders.OVERLAY,
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Fetch sync audit logs for a platform connection.
 * @param {string} id - Connection UUID
 * @param {number} [offset] - Pagination offset
 * @param {number} [limit] - Pagination limit
 * @param {string} [search] - Search filter
 * @param {string} [sortBy] - Sorting criteria
 * @param {boolean} [minify=true] - Minify response payload
 * @returns {Promise<Object>} Logs response
 */
export const getConnectionLogs = async (id, offset, limit, search, sortBy, minify = true) => {
  return await axios('get', `${PLATFORM_CONNECTIONS}/${id}/logs`, {}, {
    loader: loaders.NONE,
    server: microServices.DE,
    params: {
      t: Date.now(),
      ...(offset !== undefined ? { offset } : {}),
      ...(limit !== undefined ? { limit } : {}),
      ...(search !== undefined && search !== '' ? { search } : {}),
      ...(sortBy !== undefined && sortBy !== '' ? { sortBy } : {}),
      ...(minify !== undefined ? { minify } : {})
    }
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Disconnect (delete) a platform connection.
 * @param {string} id - Connection UUID
 * @returns {Promise<Object>} Disconnect response
 */
export const disconnectPlatformConnection = async (id) => {
  return await axios('delete', `${PLATFORM_CONNECTIONS}/${id}`, {}, {
    loader: loaders.OVERLAY,
    successMessage: 'Integration Disconnected Successfully',
    server: microServices.DE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Set platform connection as default.
 * @param {string} id - Connection UUID
 * @returns {Promise<Object>} Set default response
 */
export const setPlatformConnectionDefault = async (id) => {
  return await axios('put', `${PLATFORM_CONNECTIONS}/${id}/default`, {}, {
    loader: loaders.OVERLAY,
    successMessage: 'Default Connection Updated Successfully',
    server: microServices.DE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Fetch dynamic Accounts list from platform.
 * @param {string} id - Connection UUID
 * @returns {Promise<Array>} Accounts list
 */
export const getPlatformAccounts = async (id) => {
  return await axios('get', `${PLATFORM_CONNECTIONS}/${id}/accounts`, {}, {
    loader: loaders.NONE,
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Fetch dynamic Tax Rates list from platform.
 * @param {string} id - Connection UUID
 * @returns {Promise<Array>} Tax rates list
 */
export const getPlatformTaxRates = async (id) => {
  return await axios('get', `${PLATFORM_CONNECTIONS}/${id}/tax-rates`, {}, {
    loader: loaders.NONE,
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Fetch dynamic Products/Services list from platform.
 * @param {string} id - Connection UUID
 * @returns {Promise<Array>} Products/services list
 */
export const getPlatformItems = async (id) => {
  return await axios('get', `${PLATFORM_CONNECTIONS}/${id}/items`, {}, {
    loader: loaders.NONE,
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Fetch background sync job status.
 * @param {string} connectionId - Connection UUID
 * @param {string} jobId - Background job UUID
 * @returns {Promise<Object>} Sync job status response
 */
export const getSyncJobStatus = async (connectionId, jobId) => {
  return await axios('get', `${PLATFORM_CONNECTIONS}/${connectionId}/sync-status/${jobId}`, {}, {
    loader: loaders.NONE,
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Export claims/invoices in bulk to accounting platform.
 * @param {string} connectionId - Connection UUID
 * @param {Array<string>} invoiceIds - Invoice / claim UUIDs
 * @param {Object} [settingsOverride] - Optional settings overrides
 * @param {boolean} [force=false] - Whether to force re-sync
 * @returns {Promise<Object>} Bulk export response
 */
export const exportInvoicesBulk = async (connectionId, invoiceIds, settingsOverride, force = false) => {
  return await axios('post', `${PLATFORM_CONNECTIONS}/${connectionId}/export-invoice/bulk`, {
    invoice_ids: invoiceIds,
    settings_override: settingsOverride || undefined,
    force
  }, {
    loader: loaders.NONE,
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Export bills in bulk to accounting platform.
 * @param {string} connectionId - Connection UUID
 * @param {Array<string>} billIds - Bill / PO UUIDs
 * @param {Object} [settingsOverride] - Optional settings overrides
 * @param {boolean} [force=false] - Whether to force re-sync
 * @returns {Promise<Object>} Bulk bill export response
 */
export const exportBillsBulk = async (connectionId, billIds, settingsOverride, force = false) => {
  return await axios('post', `${PLATFORM_CONNECTIONS}/${connectionId}/export-bill/bulk`, {
    bill_ids: billIds,
    settings_override: settingsOverride || undefined,
    force
  }, {
    loader: loaders.NONE,
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Cancel an active background sync job.
 * @param {string} connectionId - Connection UUID
 * @param {string} jobId - Background job UUID
 * @returns {Promise<Object>} Cancellation response
 */
export const cancelSyncJob = async (connectionId, jobId) => {
  return await axios('post', `${PLATFORM_CONNECTIONS}/${connectionId}/sync/cancel`, {
    jobId
  }, {
    loader: loaders.NONE,
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Push a purchase order or work order to accounting platform.
 * @param {string} connectionId - Connection UUID
 * @param {string} poUuid - Purchase order UUID
 * @returns {Promise<Object>} Export response
 */
export const exportPurchaseOrder = async (connectionId, poUuid) => {
  return await axios('post', `${PLATFORM_CONNECTIONS}/${connectionId}/export-purchase-order`, {
    po_uuid: poUuid
  }, {
    loader: loaders.NONE,
    successMessage: 'Order push queued successfully',
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Fetch customer sync linkage status.
 * @param {string} connectionId - Connection UUID
 * @param {string} customerId - Customer internal UUID
 * @returns {Promise<Object>} Customer sync status
 */
export const getCustomerSyncStatus = async (connectionId, customerId) => {
  return await axios('get', `${PLATFORM_CONNECTIONS}/${connectionId}/customer-sync-status/${customerId}?t=${Date.now()}`, {}, {
    loader: loaders.NONE,
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Fetch sync statuses for multiple entities in bulk.
 * @param {string} connectionId - Connection UUID
 * @param {string} entityType - Entity type ('customer' | 'supplier' | 'contractor' | 'invoice' | 'bill')
 * @param {Array<string>} entityIds - Entity IDs
 * @returns {Promise<Object>} Entity sync statuses map
 */
export const getEntitiesSyncStatus = async (connectionId, entityType, entityIds) => {
  return await axios('post', `${PLATFORM_CONNECTIONS}/${connectionId}/entities-sync-status`, {
    entity_type: entityType,
    entity_ids: entityIds
  }, {
    loader: loaders.NONE,
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Fetch supported platform transaction statuses.
 * @param {string} connectionId - Connection UUID
 * @returns {Promise<Array>} Transaction statuses list
 */
export const getPlatformTransactionStatuses = async (connectionId) => {
  return await axios('get', `${PLATFORM_CONNECTIONS}/${connectionId}/transaction-statuses?t=${Date.now()}`, {}, {
    loader: loaders.NONE,
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};

/**
 * Fetch supported platform currencies.
 * @param {string} connectionId - Connection UUID
 * @returns {Promise<Array>} Currencies list
 */
export const getPlatformCurrencies = async (connectionId) => {
  return await axios('get', `${PLATFORM_CONNECTIONS}/${connectionId}/currencies?t=${Date.now()}`, {}, {
    loader: loaders.NONE,
    server: microServices.DE_CORE
  }).then(
    data => data,
    error => Promise.reject(error)
  );
};
